/**
 * Единый источник пунктов меню врача и логики доступа/активного маршрута.
 * Используется в Personal/Menu.vue и Header/Menu.vue.
 */

import {normalizeMenuPath} from '~/composables/useClinicMenu';
import {isDoctorPreviewPathActiveForMenuLink} from '~/utils/personalSectionPreview';

export interface DoctorMenuItem {
    link: string;
    name: string;
    icon?: string;
    separatorAfter?: boolean;
    verticalSeparators?: boolean;
    requiresOnline?: boolean;
    requiresEducation?: boolean;
    requiresPublished?: boolean;
    /** Скрыть для врачей, привязанных к клинике (не индивидуальных) */
    hideForClinicDoctors?: boolean;
}

export type ComputedDoctorMenuItem = DoctorMenuItem & {
    isActive: boolean;
    isDisabled: boolean;
    disableReason: string;
};

const ROUTE_ALIASES_BY_LINK: Record<string, RegExp[]> = {
    '/profile-doctor/schedule/': [/^\/profile-doctor\/schedule-date\/?$/],
    '/profile-doctor/history/': [/^\/profile-doctor\/appointment-\d+\/?$/],
};

function isRouteMatchedByAlias(path: string, link: string): boolean {
    const aliases = ROUTE_ALIASES_BY_LINK[link];
    if (!aliases?.length) return false;
    return aliases.some((pattern) => pattern.test(path));
}

export function isDoctorMenuItemActive(routePath: string, item: Pick<DoctorMenuItem, 'link'>): boolean {
    if (isDoctorPreviewPathActiveForMenuLink(routePath, item.link)) {
        return true;
    }
    const path = normalizeMenuPath(routePath);
    const link = normalizeMenuPath(item.link);
    const isPrefixMatch = path.startsWith(link);
    return isPrefixMatch || isRouteMatchedByAlias(path, item.link);
}

export type DoctorMenuGates = {
    isPublished: boolean;
    hasOnlineReception: boolean;
    hasScheduleReception: boolean;
    isIndividualDoctor: boolean;
    hasEducationAndDiploma: boolean;
    isStudent: boolean;
};

export function isDoctorMenuItemDisabled(item: DoctorMenuItem, gates: DoctorMenuGates): boolean {
    // Расписание/консультации доступны при онлайн или выезде + публикации.
    if (item.requiresOnline === true) {
        return !gates.hasScheduleReception || !gates.isPublished;
    }

    if (!gates.isIndividualDoctor) return false;

    if (item.requiresEducation === true && !gates.hasEducationAndDiploma) {
        return true;
    }

    return item.requiresPublished === true && !gates.isPublished;
}

export function getDoctorMenuDisableReason(item: DoctorMenuItem, gates: DoctorMenuGates): string {
    if (item.requiresOnline === true && !gates.hasScheduleReception) {
        return 'В профиле выберите тип приёма и укажите стоимость. Для выезда на дом также выберите услугу. Затем вернитесь сюда и заполните расписание.';
    }

    if (item.requiresOnline === true && !gates.isPublished) {
        return 'Завершите заполнение профиля и дождитесь проверки. Обычно это занимает до 24 часов.';
    }

    if (item.requiresPublished === true && !gates.isPublished) {
        return 'Дождитесь проверки ваших данных из формы регистрации. Обычно это занимает до 24 часов.';
    }

    if (item.requiresEducation === true && !gates.hasEducationAndDiploma) {
        if (gates.isStudent) {
            return 'В Профиле заполните данные в блоке «Образование» (учебное заведение, справка или студенческий билет). Если вы уже заполнили все данные, пожалуйста, дождитесь их проверки.';
        }
        return 'В Профиле заполните данные в блоке «Образование» (вуз, год окончания, диплом). Если вы уже заполнили все данные, пожалуйста, дождитесь их проверки.';
    }
    return '';
}

export function getDoctorMenuBlockedModalTitle(item: DoctorMenuItem, gates: DoctorMenuGates): string {
    if (item.requiresEducation === true && !gates.hasEducationAndDiploma) {
        return 'Укажите образование для доступа к обучающим материалам';
    }
    if (item.requiresOnline === true && !gates.hasScheduleReception) {
        return 'Настройте онлайн-приём или выезд для доступа в раздел';
    }
    if (item.requiresPublished === true && !gates.isPublished) {
        return 'Раздел скоро будет доступен';
    }
    if (item.requiresOnline === true && !gates.isPublished) {
        return 'Раздел скоро будет доступен';
    }
    return 'Раздел доступен подтверждённым врачам';
}

/**
 * Пункты меню врача (название «Консультации» зависит от того, привязан ли врач к клинике).
 */
export function getDoctorMenuItems(isIndividualDoctor: boolean): DoctorMenuItem[] {
    return [
        {link: '/profile-doctor/schedule/', name: 'Расписание', icon: 'schedule', requiresOnline: true},
        {
            link: '/profile-doctor/history/',
            name: isIndividualDoctor ? 'Консультации' : 'Консультации и записи',
            icon: 'history',
            separatorAfter: true,
            requiresOnline: true,
        },
        {link: '/profile-doctor/widget/', name: 'Виджет', icon: 'widget', requiresOnline: true},
        {
            link: '/profile-doctor/finances/',
            name: 'Финансы',
            icon: 'finances',
            requiresOnline: true,
            hideForClinicDoctors: true
        },
        {link: '/profile-doctor/statistics/', name: 'Аналитика', icon: 'statistics', requiresOnline: true},
        {link: '/profile-doctor/reviews/', name: 'Отзывы', icon: 'reviews', requiresPublished: true},
        {
            link: '/profile-doctor/partner-programs/',
            name: 'Партнерские программы',
            icon: 'partner-programs',
            requiresPublished: true,
            hideForClinicDoctors: true,
        },
        {
            link: '/profile-doctor/referral-program/',
            name: 'Реферальная программа',
            icon: 'referral-program',
            requiresPublished: true,
            hideForClinicDoctors: true,
        },
        {
            link: '/profile-doctor/education/',
            name: 'Обучение',
            icon: 'education',
            verticalSeparators: true,
            requiresEducation: true
        },
        {link: '/profile-doctor/edit/', name: 'Профиль', icon: 'settings'},
    ];
}

export function filterDoctorMenuItemsForViewer(
    items: DoctorMenuItem[],
    isIndividualDoctor: boolean,
): DoctorMenuItem[] {
    return items.filter((item) => !(item.hideForClinicDoctors && !isIndividualDoctor));
}

export function useDoctorProfileGates() {
    const {type} = usePersonalType();
    const authStore = useAuthStore();
    const {userProfileData} = storeToRefs(authStore);

    const isPublished = computed(() => {
        if (type.value !== 'doctor') return true;
        return userProfileData.value?.status === 'publish';
    });

    const hasOnlineReception = computed(() => {
        if (type.value !== 'doctor') return true;
        const receptionType = userProfileData.value?.receptionType;
        return receptionType === 'online' || receptionType === 'full';
    });

    const hasVisitReception = computed(() => {
        if (type.value !== 'doctor') return false;
        return Boolean(userProfileData.value?.visitEnabled);
    });

    const hasScheduleReception = computed(() => {
        if (type.value !== 'doctor') return true;
        return hasOnlineReception.value || hasVisitReception.value;
    });

    const isIndividualDoctor = computed(() => {
        if (type.value !== 'doctor') return false;
        if (!userProfileData.value) return false;
        return !userProfileData.value.clinic?.id;
    });

    const hasEducationAndDiploma = computed(() => {
        if (type.value !== 'doctor') return true;
        const educations = userProfileData.value?.educations ?? [];
        const hasEducation = educations.length > 0;
        const hasDiploma = Boolean(userProfileData.value?.hasVerifiedDiploma);
        return hasEducation && hasDiploma;
    });

    const isStudent = computed(() => {
        if (type.value !== 'doctor') return false;
        return userProfileData.value?.specialistType === 'student';
    });

    const gates = computed(
        (): DoctorMenuGates => ({
            isPublished: type.value !== 'doctor' ? true : isPublished.value,
            hasOnlineReception: type.value !== 'doctor' ? true : hasOnlineReception.value,
            hasScheduleReception: type.value !== 'doctor' ? true : hasScheduleReception.value,
            isIndividualDoctor: type.value !== 'doctor' ? false : isIndividualDoctor.value,
            hasEducationAndDiploma: type.value !== 'doctor' ? true : hasEducationAndDiploma.value,
            isStudent: type.value !== 'doctor' ? false : isStudent.value,
        }),
    );

    return {
        gates,
        isIndividualDoctor,
        isPublished,
        hasOnlineReception,
        hasVisitReception,
        hasScheduleReception,
        hasEducationAndDiploma,
        isStudent,
    };
}

export function useDoctorMenuComputedItems() {
    const route = useRoute();
    const {gates} = useDoctorProfileGates();

    return computed((): ComputedDoctorMenuItem[] => {
        const g = gates.value;
        const raw = getDoctorMenuItems(g.isIndividualDoctor);
        const filtered = filterDoctorMenuItemsForViewer(raw, g.isIndividualDoctor);

        return filtered.map((item) => ({
            ...item,
            isActive: isDoctorMenuItemActive(route.path, item),
            isDisabled: isDoctorMenuItemDisabled(item, g),
            disableReason: getDoctorMenuDisableReason(item, g),
        }));
    });
}
